// ============================================
// Partnership Consent Section
// CS_CONSENT_-_Partnership.pdf (Partnership HealthPlan)
// Matches PDF fields exactly
// ============================================

import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormSubmission } from '../../types';
import FormField from '../ui/FormField';
import { useLanguage } from '../../i18n/LanguageContext';

const PartnershipSection: React.FC = () => {
  const { register, watch, formState: { errors } } = useFormContext<FormSubmission>();
  const { t } = useLanguage();

  const expirationCustom = watch('partnershipConsent.expirationCustomInitial');

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <h2 className="text-lg font-semibold text-blue-900">{t('pc.title')}</h2>
        <p className="text-sm text-blue-700">{t('pc.subtitle')}</p>
      </div>

      {/* Full Legal Text - Introductory Paragraph */}
      <div className="text-sm text-gray-700 leading-relaxed">
        <p>{t('pc.introParagraph')}</p>
      </div>

      {/* Authorization Statement */}
      <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
        <p className="text-sm text-gray-800 font-medium">
          {t('pc.authStatement')}
        </p>
      </div>

      {/* Choose ONE of the following options */}
      <div className="border-t pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">
          {t('pc.chooseOne')}
        </h3>

        <div className="space-y-6">
          {/* Consent for communication by CS Program */}
          <div className="flex items-start gap-4 p-4 bg-white rounded-lg border-2 border-gray-300">
            <div className="w-24 flex-shrink-0">
              <label className="text-xs text-gray-600 block mb-1">{t('pc.initialHere')}</label>
              <input
                type="text"
                {...register('partnershipConsent.consentInitial')}
                className="form-input w-full rounded-md border-gray-300 text-center uppercase font-bold"
                placeholder="___"
                maxLength={4}
              />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-800 mb-2">
                <strong>{t('pc.consentCommHeading')}</strong> {t('pc.consentCommText')}
              </p>
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>{t('pc.consentComm1')}</li>
                <li>{t('pc.consentComm2')}</li>
                <li>{t('pc.consentComm3')}</li>
                <li>{t('pc.consentComm4')}</li>
                <li>{t('pc.consentComm5')}</li>
                <li>{t('pc.consentComm6')}</li>
              </ul>
            </div>
          </div>

          {/* Decline to participate in CS */}
          <div className="flex items-start gap-4 p-4 bg-white rounded-lg border-2 border-gray-300">
            <div className="w-24 flex-shrink-0">
              <label className="text-xs text-gray-600 block mb-1">{t('pc.initialHere')}</label>
              <input
                type="text"
                {...register('partnershipConsent.declineInitial')}
                className="form-input w-full rounded-md border-gray-300 text-center uppercase font-bold"
                placeholder="___"
                maxLength={4}
              />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-800">
                <strong>{t('pc.declineHeading')}</strong> {t('pc.declineText')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Further authorization section */}
      <div className="border-t pt-6">
        <p className="text-sm text-gray-800 mb-4">
          {t('pc.furtherAuthIntro')}
        </p>

        <div className="space-y-4">
          {/* Mental Health */}
          <div className="flex items-start gap-4 p-4 bg-yellow-50 rounded-lg border border-yellow-300">
            <div className="w-24 flex-shrink-0">
              <label className="text-xs text-gray-600 block mb-1">{t('pc.initialHere')}</label>
              <input
                type="text"
                {...register('partnershipConsent.mentalHealthInitial')}
                className="form-input w-full rounded-md border-gray-300 text-center uppercase"
                placeholder="___"
                maxLength={4}
              />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-800">
                {t('pc.mentalHealthText')}
              </p>
            </div>
          </div>

          {/* HIV Test Results */}
          <div className="flex items-start gap-4 p-4 bg-yellow-50 rounded-lg border border-yellow-300">
            <div className="w-24 flex-shrink-0">
              <label className="text-xs text-gray-600 block mb-1">{t('pc.initialHere')}</label>
              <input
                type="text"
                {...register('partnershipConsent.hivInitial')}
                className="form-input w-full rounded-md border-gray-300 text-center uppercase"
                placeholder="___"
                maxLength={4}
              />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-800">
                {t('pc.hivText')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Substance Use Disorder Information */}
      <div className="border-t pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t('pc.sudHeading')}</h3>
        <div className="text-sm text-gray-700 leading-relaxed mb-4 bg-gray-50 p-4 rounded-lg">
          <p>{t('pc.sudText')}</p>
        </div>

        <div className="flex items-start gap-4 p-4 bg-yellow-50 rounded-lg border border-yellow-300">
          <div className="w-24 flex-shrink-0">
            <label className="text-xs text-gray-600 block mb-1">{t('pc.initialHere')}</label>
            <input
              type="text"
              {...register('partnershipConsent.substanceUseInitial')}
              className="form-input w-full rounded-md border-gray-300 text-center uppercase"
              placeholder="___"
              maxLength={4}
            />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-800">
              {t('pc.sudInitialText')}
            </p>
          </div>
        </div>
      </div>

      {/* Expiration of Form */}
      <div className="border-t pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">{t('pc.expirationHeading')}</h3>
        <p className="text-sm text-gray-800 mb-4">{t('pc.expirationChooseOne')}</p>

        <div className="space-y-4">
          {/* Standard Expiration */}
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-300">
            <div className="w-24 flex-shrink-0">
              <label className="text-xs text-gray-600 block mb-1">{t('pc.initialHere')}</label>
              <input
                type="text"
                {...register('partnershipConsent.expirationStandardInitial')}
                className="form-input w-full rounded-md border-gray-300 text-center uppercase"
                placeholder="___"
                maxLength={4}
              />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-800">
                <strong>{t('pc.expirationStandard')}</strong> {t('pc.expirationStandardText')}
              </p>
            </div>
          </div>

          {/* Custom Expiration */}
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-300">
            <div className="w-24 flex-shrink-0">
              <label className="text-xs text-gray-600 block mb-1">{t('pc.initialHere')}</label>
              <input
                type="text"
                {...register('partnershipConsent.expirationCustomInitial')}
                className="form-input w-full rounded-md border-gray-300 text-center uppercase"
                placeholder="___"
                maxLength={4}
              />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-800 mb-2">
                <strong>{t('pc.expirationCustom')}</strong> {t('pc.expirationCustomText')}
                {expirationCustom && (
                  <span className="inline-block ml-2">
                    <input
                      type="date"
                      min="1900-01-01"
                      {...register('partnershipConsent.customExpirationDate')}
                      className="form-input rounded-md border-gray-300"
                    />
                  </span>
                )}
                {!expirationCustom && <span className="inline-block ml-1">_______________</span>}
                {t('pc.expirationCustomNote')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* I understand that: section */}
      <div className="border-t pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t('pc.understandHeading')}</h3>
        <ul className="text-sm text-gray-700 space-y-3 list-disc ml-6">
          <li>
            {t('pc.understand1')}
            <div className="ml-4 mt-1 text-gray-600 whitespace-pre-line">
              {t('pc.understand1Address')}
            </div>
          </li>
          <li>{t('pc.understand2')}</li>
          <li>{t('pc.understand3')}</li>
          <li>{t('pc.understand4')}</li>
          <li>{t('pc.understand5')}</li>
          <li>{t('pc.understand6')}</li>
          <li>{t('pc.understand7')}</li>
          <li>{t('pc.understand8')}</li>
          <li>{t('pc.understand9')}</li>
          <li>{t('pc.understand10')}</li>
        </ul>
      </div>

      {/* Signature Note */}
      <div className="border-t pt-6">
        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
          <p className="text-sm text-blue-900 font-medium">
            {t('pc.signatureNote')}
          </p>
          <p className="text-xs text-blue-700 mt-2">
            {t('pc.signatureNoteDetail')}
          </p>
        </div>
      </div>

      {/* Signature Instructions */}
      <div className="border-t pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-3">{t('pc.signatureMemberHeading')}</h3>
        <p className="text-sm text-gray-700 mb-4">
          {t('pc.signatureInstruction')}
        </p>

        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-xs text-gray-600 mb-2">
            ✓ {t('pc.signatureCheckNote1')}<br />
            ✓ {t('pc.signatureCheckNote2')}
          </p>

          <div className="mt-4">
            <FormField label={t('pc.repName')}>
              <input
                type="text"
                {...register('partnershipConsent.repName')}
                className="form-input w-full rounded-md border-gray-300"
                placeholder={t('pc.repNamePlaceholder')}
              />
            </FormField>
          </div>
        </div>
      </div>

      {/* Legacy fields (hidden for backward compatibility) */}
      <input type="hidden" {...register('partnershipConsent.organizationName')} value="Partnership HealthPlan of California" />
    </div>
  );
};

export default PartnershipSection;
